import { EffectIcons } from 'chessboard/plugins/effects/renderer/icons';
import { EffectTypes } from 'chessboard/plugins/effects/config/types';
import type JCE from '@chesscom/js-chess-engine';
import type { Game } from 'chessboard/model/game/types';
import type { MoveListComponent } from '../config/types';

export type AnnotationDetails = {
  color?: string;
  icon?: string;
  inlineText: string;
};

type GetPlyAnnotationDetailsOptions = {
  /**
   * disables text, color, and icons from a node annotation (still allows custom overrides)
   */
  disableAnnotations?: boolean;
  /**
   * show annotations as text instead of icons (still allows custom overrides)
   */
  showAllAnnotationsAsText?: boolean;
  /**
   * disables icons entirely (from annotations or custom overrides), shows annotations as text instead
   */
  disableAllIcons?: boolean;
  /**
   * disable custom colors from overrides or node.customColor value (still allows colors from annotations)
   */
  disableCustomColors?: boolean;
  /**
   * overrides for color and icon that take priority over annotation values
   */
  nodeEffectOverrides?: MoveListComponent.NodeEffectOverridesCollection;
};

const ANNOTATION_ICON_DETAILS: { [key: string]: Omit<AnnotationDetails, 'inlineText'> } = {
  '!!': {
    icon: EffectIcons[EffectTypes.Brilliant],
    color: '#1baca6',
  },
  '!': {
    icon: EffectIcons[EffectTypes.GreatFind],
    color: '#5c8bb0',
  },
  '?': {
    icon: EffectIcons[EffectTypes.Mistake],
    color: '#e6912c',
  },
  '??': {
    icon: EffectIcons[EffectTypes.Blunder],
    color: '#b33430',
  },
  '?!': {
    icon: EffectIcons[EffectTypes.Inaccuracy],
    color: '#f0c15c',
  },
  '!?': {
    icon: EffectIcons[EffectTypes.Interesting],
    color: '#7979a1',
  },
  '☒': {
    icon: EffectIcons[EffectTypes.Miss],
    color: '#ee6b55',
  },
};

/**
 * Returns properties describing how to render annotation details for a ply
 */
export function getPlyAnnotationDetails(
  node: MoveListComponent.NodeModel,
  options: GetPlyAnnotationDetailsOptions = {},
): AnnotationDetails {
  if (!node) {
    return { inlineText: '' };
  }

  const mainAnnotation: string = node.annotation ?? '';
  const additionalAnnotation: string = node.additionalAnnotation?.[0] ?? '';

  if (options.disableAnnotations || (!mainAnnotation && !additionalAnnotation)) {
    return withOverrides({ inlineText: '' }, node, options);
  }

  const mainAnnotationIcon = ANNOTATION_ICON_DETAILS[mainAnnotation];
  const additionalAnnotationIcon = ANNOTATION_ICON_DETAILS[additionalAnnotation];

  const icon = mainAnnotationIcon ?? additionalAnnotationIcon;

  if (shouldShowAnnotationIconAsText(node.ids, icon?.icon, options)) {
    // concat the annotations as text if we are not using the icon for some reason
    return withOverrides(
      {
        color: icon?.color,
        inlineText: `${mainAnnotation} ${additionalAnnotation}`.trim(),
      },
      node,
      options,
    );
  }

  return withOverrides(
    {
      ...icon,
      // set additional annotation as inline text if it's not an icon and/or an icon is already present
      inlineText: !icon || icon !== additionalAnnotationIcon ? additionalAnnotation : '',
    },
    node,
    options,
  );
}

/**
 * Returns new annotation details with any overrides
 */
function withOverrides(
  annotationDetails: AnnotationDetails,
  node: Game.Move.Node,
  options: GetPlyAnnotationDetailsOptions = {},
): AnnotationDetails {
  // overrides: null = reset to default, undefined = dont modify, value = set to value

  const { textColor, effectType } =
    options.nodeEffectOverrides?.[`${node.ids.line}-${node.ids.move}`] ?? {};

  const overrides: Partial<AnnotationDetails> = {};

  if (!options.disableCustomColors) {
    if (node.customColor) {
      overrides.color = node.customColor;
    }

    // nodeEffectOverrides is prioritized over node.customColor
    if (textColor !== undefined) {
      overrides.color = textColor === null ? undefined : textColor;
    }
  }

  if (effectType !== undefined) {
    overrides.icon = effectType === null ? undefined : EffectIcons[effectType];
  }

  const returnVal = {
    ...annotationDetails,
    ...overrides,
  };

  if (options.disableAllIcons) {
    delete returnVal.icon;
  }

  return returnVal;
}

/**
 * determines if an annotation icon should be shown as text instead of an icon
 */
function shouldShowAnnotationIconAsText(
  nodeIds: JCE.Ids,
  icon: string | undefined,
  options: GetPlyAnnotationDetailsOptions,
) {
  if (options.showAllAnnotationsAsText || options.disableAllIcons) {
    // show text annotation if disabling those icons
    return true;
  }

  const { effectType } = options.nodeEffectOverrides?.[`${nodeIds.line}-${nodeIds.move}`] ?? {};

  // show text annotation if the icon is being unset or overridden by a different icon
  return effectType === null || (effectType && EffectIcons[effectType] !== icon);
}
